import { memo, useState, useCallback, useEffect, useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { FileDragHandlers } from "@/hooks/useFileDragAndDrop";
import { ChevronDown, ChevronUp } from "lucide-react";
import { FilePreviewInline } from "@/components/file-preview/FilePreviewInline";
import { cn } from "@/lib/utils/cn";
import { buildFileUrl } from "@/lib/utils/buildFileUrl";

interface ContractorCardFilePreviewProps {
  filename: string;
  mimetype: string;
  isPending?: boolean;
  isDragging: boolean;
  dragHandlers: FileDragHandlers;
  fileUrl?: string;
  s3Key?: string;
  pendingFileContent?: string;
}

export const ContractorCardFilePreview = memo(
  ({
    filename,
    mimetype,
    isPending,
    isDragging,
    dragHandlers,
    fileUrl,
    s3Key,
    pendingFileContent,
  }: ContractorCardFilePreviewProps) => {
    const { t } = useTranslation();

    const resolvedFileUrl = useMemo(() => {
      if (pendingFileContent) {
        return `data:${mimetype};base64,${pendingFileContent}`;
      }
      return fileUrl || (s3Key ? buildFileUrl(s3Key) : null);
    }, [pendingFileContent, mimetype, fileUrl, s3Key]);

    const hasFile = !!resolvedFileUrl;

    const [isExpanded, setIsExpanded] = useState(hasFile);

    useEffect(() => {
      if (hasFile) {
        setIsExpanded(true);
      }
    }, [hasFile]);

    const toggleInlinePreview = useCallback(() => {
      setIsExpanded((prev) => !prev);
    }, []);

    return (
      <div
        className={cn(
          "rounded-lg border border-slate-200 bg-white shadow-sm",
          isDragging && "border-2 border-primary bg-primary/5",
        )}
        {...dragHandlers}
      >
        <div className="pt-3 pr-3 pl-3 space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">
              {t("contractorCards.fields.card_filename")}:
            </span>
            <span className="text-sm font-medium text-right flex-1 ml-2 truncate">
              {filename}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">
              {t("contractorCards.fields.card_mimetype")}:
            </span>
            <Badge variant="outline" className="text-xs">
              {mimetype}
            </Badge>
          </div>
          {isPending && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Status:</span>
              <Badge variant="default" className="text-xs">
                {t("contractorCards.values.new")}
              </Badge>
            </div>
          )}
          {resolvedFileUrl && (
            <div
              className="flex items-center justify-center py-2 border-t border-slate-100 cursor-pointer hover:bg-slate-50 transition-colors -mx-3 px-3"
              onClick={toggleInlinePreview}
            >
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 sm:h-6 sm:w-6 pointer-events-none"
                disabled={!resolvedFileUrl}
              >
                {isExpanded ? (
                  <ChevronUp className="h-4 w-4 sm:h-3.5 sm:w-3.5" />
                ) : (
                  <ChevronDown className="h-4 w-4 sm:h-3.5 sm:w-3.5" />
                )}
              </Button>
            </div>
          )}
        </div>
        {isExpanded && resolvedFileUrl && (
          <div className="border-t border-slate-100 px-4 py-3">
            <FilePreviewInline fileUrl={resolvedFileUrl} fileName={filename} />
          </div>
        )}
      </div>
    );
  },
);
